import React, { FC, useContext, useEffect, useState } from "react";
import {
  alpha,
  Box,
  Card,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import SimpleModal from "../../components/Modals/SimpleModal";
import { RootState, useDispatch, useSelector } from "../../store";
import {
  clearDetails,
  GrantsFileRecord,
  getGrantDetails,
} from "../../slices/grants";
import UserContext, {
  isUserAdminOrSemiAdminWith,
  ServerOrgRoles,
} from "../../services/UserContext";
import { RequestStatus } from "../../utils/Helpers/fetchStatus";
import FileListItem from "../../components/Widgets/FileListItem";
import Button from "@mui/material/Button";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import {
  createAuthenticatedRequest,
  getFullUrl,
} from "../../configs/axios-export.custom";
import { saveAs } from "file-saver";
import toast from "react-hot-toast";
import CustomTable from "../../components/MaterialTable/Table";
import {
  getEmptyStringWithCurrencyIfNull,
  getSortableColumnPropertyName,
} from "./grantsUtils";
import LinkIcon from "@mui/icons-material/Link";
import {
  getGrantsColumnsConfig,
  getGrantsStickyColumnsConfig,
} from "./columns.config";
import RowActions from "./RowActions";
import { removeHtmlTags } from "../../services/dataParser";
import { GrantsResourceServer } from "../../configs/resources";
import { extractDataFromSearchParams } from "../../utils/Helpers/extractDataFromSearchParams";
import { useHistory } from "react-router-dom";

interface GrantsDetailsAndFilesModalProps {
  open: boolean;
  openFor: any;
  handleCloseDetailsModal: () => void;
}

interface SortOption {
  sortField: string;
  label: string;
  sortOrder: number;
}

const sortOptions: Array<SortOption> = [
  { sortField: "createdOn", label: "Most recent", sortOrder: -1 },
  { sortField: "createdOn", label: "Oldest", sortOrder: 1 },
  { sortField: "fileName", label: "Name: A → Z", sortOrder: 1 },
  { sortField: "fileName", label: "Name: Z → A", sortOrder: -1 },
  { sortField: "disable-sort", label: "Disable sorting", sortOrder: 0 },
];

const GrantsDetailsAndFilesModal: FC<GrantsDetailsAndFilesModalProps> = (
  props,
) => {
  const { open, openFor, handleCloseDetailsModal } = props;

  const dispatch = useDispatch();
  const context = useContext(UserContext);
  const MUITheme = useTheme();
  const history = useHistory();

  const canEdit = isUserAdminOrSemiAdminWith(
    context,
    ServerOrgRoles.EditPrimesAndQuarterlyCallInfo,
  );

  const { fetchStatus, files, matchingFiles, records } = useSelector(
    (state: RootState) => state.grants.details,
  );

  const [selectedTab, setSelectedTab] = useState<"files" | "recordHistory">(
    "files",
  );
  const [selectedSortOption, setSelectedSortOption] = useState<SortOption>(
    sortOptions[0],
  );
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const handleDownloadAll = async () => {
    setIsDownloading(true);
    axios
      .post(
        getFullUrl(`/api/grant/details/${openFor.id}/getzip`, {
          useDedicatedEnvironment: true,
        }),
        {},
        {
          ...createAuthenticatedRequest(context),
          responseType: "blob",
        } as any,
      )
      .then(async (response) => {
        let fileName = `export-${new Date().valueOf()}.zip`;
        try {
          fileName = response.headers["content-disposition"]
            .split(";")[1]
            .replace("filename=", "")
            .replaceAll('"', "")
            .trim();
        } catch {
          /* NO-OP */
        }

        // in deployed version the response is still getting converted back
        // to b64 so need to un-load that. if running web server locally the
        // response is already decoded so can directly save response.data instead
        // of this BS
        // const decoded = atob(await response.data.text());
        // const array = new Uint8Array(decoded.length);
        // for (let i = 0; i < decoded.length; i++) {
        //   array[i] = decoded.charCodeAt(i);
        // }

        // saveAs(
        //   new Blob([array], { type: "application/zip" }),
        //   fileName
        // );

        saveAs(new Blob([response.data]), fileName);
        setIsDownloading(false);
      })
      .catch((error) => {
        toast.error("Error fetching data.");
        setIsDownloading(false);
      });
  };

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setSelectedTab("files");
        dispatch(clearDetails());
      }, 150);
    } else {
      const data = extractDataFromSearchParams(history.location.search, [
        "fileKeyword",
        "fileName",
      ]);
      if (openFor?.id) {
        dispatch(
          getGrantDetails({
            context,
            params: {
              id: openFor.id,
              ...data,
            },
          }),
        );
      }
    }
  }, [open]);

  return (
    <SimpleModal
      open={open}
      handleClose={handleCloseDetailsModal}
      title={"Opportunity Details"}
      minWidth={"800px"}
      loading={RequestStatus.isFetching(fetchStatus)}
      disableCancelButton
      handleAccept={handleCloseDetailsModal}
      acceptLabel={"Close"}
    >
      <Card>
        <Box
          sx={{
            minHeight: "144px",
            display: "flex",
          }}
        >
          {openFor !== null && (
            <CustomTable
              config={{
                columns: getGrantsColumnsConfig(getSortableColumnPropertyName)
                  .filter((column) =>
                    column?.hiddenBasedOnRole ? canEdit : true,
                  )
                  .filter((column) => column?.propertyName !== "adminActions"),
                stickyColumns: getGrantsStickyColumnsConfig(false, true),
                data: [openFor].map((item) => ({
                  ...item,
                  actions: (
                    <Tooltip title={"Open in new tab"}>
                      <span>
                        <IconButton
                          sx={{
                            border: "solid 2px",
                            backgroundColor: alpha(
                              MUITheme.palette.primary.main,
                              0.1,
                            ),
                          }}
                          color={"primary"}
                          component={"a"}
                          // href={`https://www.grants.gov/web/grants/view-opportunity.html?oppId=${item?.id}`}
                          href={`https://grants.gov/search-results-detail/${item?.id}`}
                          target={"_blank"}
                          rel={"noreferrer noopener"}
                        >
                          <LinkIcon sx={{ width: "24px", height: "24px" }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ),
                  cfdas: `<div>${(item.cfdas || []).map(
                    (v: string) => `<p>${v}</p>`,
                  )}</div>`,
                  costSharing: item.costSharing ? "YES" : "NO",
                  fundingActivityDescription:
                    item.fundingActivityDescription.join(", ") ?? "",
                  estimatedFundingFormatted: getEmptyStringWithCurrencyIfNull(
                    item.estimatedFundingFormatted,
                  ),
                  awardCeilingFormatted: getEmptyStringWithCurrencyIfNull(
                    item.awardCeilingFormatted,
                  ),
                  awardFloorFormatted: getEmptyStringWithCurrencyIfNull(
                    item.awardFloorFormatted,
                  ),
                  applicantTypeDescription: removeHtmlTags(
                    item.applicantTypeDescription,
                  ).replace("&ndash;", "–"),
                  agencyContactDescription: (
                    item.agencyContactDescription || ""
                  )
                    .replace(/\&nbsp;/g, "")
                    .replace(/&amp;/g, "&")
                    .replace(/&ntilde;/g, "ñ"),
                })),
                header: <Box></Box>,
                allowFullscreen: false,
                rowHeight: 82,
              }}
              TableContainerComponent={Box}
              tableContainerComponentSx={{
                mt: "-24px",
                mb: "16px",
                border: `solid 1px ${MUITheme.palette.divider}`,
              }}
              loading={false}
              onPageChange={() => {}}
              onRowsPerPageChange={() => {}}
              pagination={{
                page: 0,
                rowsPerPage: 0,
                total: 0,
              }}
              paginationDisabled={{
                disabled: true,
                disabledMessage: "",
              }}
            />
          )}
        </Box>
        <Tabs value={selectedTab}>
          <Tab
            color={"primary"}
            onClick={() => selectedTab !== "files" && setSelectedTab("files")}
            value={"files"}
            label={"Attachments"}
          />
          <Tab
            color={"primary"}
            onClick={() =>
              selectedTab !== "recordHistory" && setSelectedTab("recordHistory")
            }
            value={"recordHistory"}
            label={"grants.gov history"}
          />
        </Tabs>
        <Divider />
        <Box sx={{ py: 2, ...(selectedTab === "recordHistory" && { pb: 0 }) }}>
          {selectedTab === "files" ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                }}
              >
                <Button
                  variant={"contained"}
                  endIcon={
                    <>
                      {" "}
                      <DownloadIcon
                        sx={{
                          height: "24px !important",
                          width: "24px !important",
                        }}
                      />
                      {isDownloading && (
                        <CircularProgress size={16} sx={{ color: "white" }} />
                      )}
                    </>
                  }
                  sx={{
                    height: "56px",
                  }}
                  onClick={handleDownloadAll}
                  disabled={isDownloading || !files.length}
                >
                  Download All
                </Button>

                <TextField
                  select
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={`${selectedSortOption.sortField}-${selectedSortOption.sortOrder}`}
                  placeholder={"Select sort option"}
                  label={"Sort"}
                  sx={{
                    minWidth: "250px",
                  }}
                  onChange={(e) =>
                    setSelectedSortOption(() => {
                      const option =
                        sortOptions.find(
                          (option) =>
                            e.target.value ===
                            `${option.sortField}-${option.sortOrder}`,
                        ) ?? sortOptions[0];

                      return option;
                    })
                  }
                >
                  {sortOptions.map((option, idx) => (
                    <MenuItem
                      value={`${option.sortField}-${option.sortOrder}`}
                      key={idx}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box
                sx={{
                  height: "348px",
                  overflowY: "scroll",
                  my: 1,
                }}
              >
                {files.length > 0 ? (
                  [...files]
                    .sort((a: GrantsFileRecord, b: GrantsFileRecord) =>
                      selectedSortOption.sortField === "disable-sort"
                        ? 0
                        : a[selectedSortOption.sortField] >
                          b[selectedSortOption.sortField]
                        ? selectedSortOption.sortOrder
                        : -selectedSortOption.sortOrder,
                    )
                    .map((file, idx) => (
                      <FileListItem
                        file={{
                          name: file.fileName,
                          description: file.fileDescription,
                          createdOn: file.createdOn,
                          uri: `${GrantsResourceServer}${encodeURIComponent(
                            file.fileName,
                          )}`,
                        }}
                        highlight={{
                          show: matchingFiles.includes(file.fileName),
                          text: "(Keyword match in document)",
                        }}
                        source={"grants"}
                      />
                    ))
                ) : RequestStatus.isDone(fetchStatus) ? (
                  <Typography variant={"h6"} color={"textSecondary"}>
                    No files found
                  </Typography>
                ) : (
                  RequestStatus.isFetching(fetchStatus) && (
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <CircularProgress />
                    </Box>
                  )
                )}
              </Box>
            </>
          ) : (
            <Box
              sx={{
                minHeight: "438px",
                display: "flex",
              }}
            >
              <CustomTable
                config={{
                  columns: getGrantsColumnsConfig(
                    getSortableColumnPropertyName,
                    true,
                  )
                    .filter((column) =>
                      column?.hiddenBasedOnRole ? canEdit : true,
                    )
                    .filter(
                      (column) => column?.propertyName !== "adminActions",
                    ),
                  // stickyColumns: getGrantsStickyColumnsConfig(false, true),
                  data: records.map((item: any) => ({
                    ...item,
                    // actions: (
                    //   <Tooltip title={"Open in new tab"}>
                    //     <span>
                    //       <IconButton
                    //         sx={{
                    //           border: "solid 2px",
                    //           backgroundColor: alpha(
                    //             MUITheme.palette.primary.main,
                    //             0.1,
                    //           ),
                    //         }}
                    //         color={"primary"}
                    //         component={"a"}
                    //         href={`https://www.grants.gov/web/grants/view-opportunity.html?oppId=${item?.id}`}
                    //         target={"_blank"}
                    //         rel={"noreferrer noopener"}
                    //       >
                    //         <LinkIcon sx={{ width: "24px", height: "24px" }} />
                    //       </IconButton>
                    //     </span>
                    //   </Tooltip>
                    // ),
                    cfdas: `<div>${(item.cfdas || []).map(
                      (v: string) => `<p>${v}</p>`,
                    )}</div>`,
                    costSharing: item.costSharing ? "YES" : "NO",
                    estimatedFundingFormatted: getEmptyStringWithCurrencyIfNull(
                      item.estimatedFundingFormatted,
                    ),
                    awardCeilingFormatted: getEmptyStringWithCurrencyIfNull(
                      item.awardCeilingFormatted,
                    ),
                    awardFloorFormatted: getEmptyStringWithCurrencyIfNull(
                      item.awardFloorFormatted,
                    ),
                    applicantTypeDescription: removeHtmlTags(
                      item.applicantTypeDescription,
                    ).replace("&ndash;", "–"),
                    agencyContactDescription: (
                      item.agencyContactDescription || ""
                    )
                      .replace(/\&nbsp;/g, "")
                      .replace(/&amp;/g, "&")
                      .replace(/&ntilde;/g, "ñ"),
                  })),
                  header: <Box></Box>,
                  allowFullscreen: false,
                }}
                TableContainerComponent={Box}
                tableContainerComponentSx={{
                  mt: "-24px",
                  mb: "16px",
                }}
                loading={RequestStatus.isFetching(fetchStatus)}
                onPageChange={() => {}}
                onRowsPerPageChange={() => {}}
                pagination={{
                  page: 0,
                  rowsPerPage: 0,
                  total: 0,
                }}
                paginationDisabled={{
                  disabled: true,
                  disabledMessage: "",
                }}
              />
            </Box>
          )}
        </Box>
      </Card>
    </SimpleModal>
  );
};

export default GrantsDetailsAndFilesModal;
